import { ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Button, Card, Col, Flex, Row, Space, Statistic, Tag, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { reviewText } from '@/features/inventorization/review-i18n';
import {
  CAPTURE_SESSION_STATUSES,
  type CaptureSession,
  READINESS_STATES,
  type ReadinessSummary,
} from '@/shared/capture/capture-session';

interface ReviewSummaryCardProps {
  blockingFieldCount: number;
  emptyCount: number;
  isQueueing: boolean;
  nextIssueExists: boolean;
  onFixNextIssue: () => void;
  onQueueOffline: () => Promise<void> | void;
  parsedCount: number;
  readiness: ReadinessSummary;
  session: CaptureSession;
}

export function ReviewSummaryCard({
  blockingFieldCount,
  emptyCount,
  isQueueing,
  nextIssueExists,
  onFixNextIssue,
  onQueueOffline,
  parsedCount,
  readiness,
  session,
}: ReviewSummaryCardProps) {
  const liveTimeSpentMs = useLiveTimeSpentMs(session);
  const isUpdatingBackendMeter = Boolean(session._id);
  const readinessTag = getReadinessTag(readiness, blockingFieldCount);
  const workflowTag = getWorkflowStatusTag(session.status, isUpdatingBackendMeter, blockingFieldCount);

  return (
    <Card size="small">
      <Flex align="flex-start" gap="middle" justify="space-between" wrap>
        <Space direction="vertical" size={2}>
          <Space size="small" wrap>
            <Typography.Text strong>
              {isUpdatingBackendMeter
                ? reviewText('summary.updatingDbMeter', 'Updating DB meter')
                : reviewText('summary.newMeterDraft', 'New meter draft')}
            </Typography.Text>
            <Tag color={readinessTag.color}>{readinessTag.label}</Tag>
            {session.meterId ? <Tag color="green">DB #{session.meterId}</Tag> : null}
            {workflowTag ? <Tag color={workflowTag.color}>{workflowTag.label}</Tag> : null}
            <Tag icon={<ClockCircleOutlined />}>
              {reviewText('summary.time', 'Time')} {formatDuration(liveTimeSpentMs)}
            </Tag>
          </Space>
          <Typography.Text type="secondary">
            {isUpdatingBackendMeter
              ? reviewText('summary.localUpdateSession', 'Local update session')
              : reviewText('summary.session', 'Session')}{' '}
            {session.id.slice(0, 14)}
          </Typography.Text>
        </Space>
        <Space wrap>
          {nextIssueExists ? (
            <Button icon={<ExclamationCircleOutlined />} onClick={onFixNextIssue}>
              {reviewText('summary.fixNextIssue', 'Fix next issue')}
            </Button>
          ) : null}
          <Button
            disabled={isQueueing || session.status === CAPTURE_SESSION_STATUSES.queued}
            onClick={onQueueOffline}
            type="primary"
          >
            {session.status === CAPTURE_SESSION_STATUSES.queued
              ? reviewText('summary.submitted', 'Submitted')
              : isQueueing
                ? reviewText('summary.submitting', 'Submitting')
                : reviewText('summary.submit', 'Submit')}
          </Button>
        </Space>
      </Flex>

      <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
        <StatusCount label={reviewText('summary.valid', 'Valid')} value={readiness.validBlocks} />
        <StatusCount label={reviewText('summary.left', 'Left')} value={blockingFieldCount} />
        <StatusCount label={reviewText('summary.parsed', 'Parsed')} value={parsedCount} />
        <StatusCount label={reviewText('summary.empty', 'Empty')} value={emptyCount} />
      </Row>
    </Card>
  );
}

function StatusCount({ label, value }: { label: string; value: number }) {
  return (
    <Col xs={12} sm={6}>
      <Statistic title={label} value={value} />
    </Col>
  );
}

function getReadinessTag(readiness: ReadinessSummary, blockingFieldCount: number) {
  if (blockingFieldCount === 0) {
    return readiness.state === READINESS_STATES.warning
      ? { color: 'orange', label: reviewText('summary.warnings', 'Warnings') }
      : { color: 'green', label: reviewText('summary.ready', 'Ready') };
  }

  if (readiness.state === READINESS_STATES.warning) {
    return { color: 'orange', label: reviewText('summary.warnings', 'Warnings') };
  }

  return {
    color: 'orange',
    label:
      blockingFieldCount === 1
        ? reviewText('summary.fieldLeftOne', '1 field left')
        : reviewText('summary.fieldLeftOther', '{count} fields left', { count: blockingFieldCount }),
  };
}

function getWorkflowStatusTag(
  status: CaptureSession['status'],
  isUpdatingBackendMeter: boolean,
  blockingFieldCount: number,
) {
  if (status === CAPTURE_SESSION_STATUSES.draft) {
    return isUpdatingBackendMeter
      ? { color: 'blue', label: reviewText('workflow.localChanges', 'Local changes') }
      : null;
  }

  if (status === CAPTURE_SESSION_STATUSES.needsReview && blockingFieldCount === 0) {
    return null;
  }

  const statusBySessionStatus: Record<
    Exclude<CaptureSession['status'], typeof CAPTURE_SESSION_STATUSES.draft>,
    { color: string; label: string }
  > = {
    [CAPTURE_SESSION_STATUSES.discarded]: { color: 'default', label: reviewText('workflow.discarded', 'Discarded') },
    [CAPTURE_SESSION_STATUSES.needsReview]: {
      color: 'gold',
      label: reviewText('workflow.reviewNeeded', 'Review needed'),
    },
    [CAPTURE_SESSION_STATUSES.queued]: { color: 'blue', label: reviewText('workflow.submitted', 'Submitted') },
    [CAPTURE_SESSION_STATUSES.ready]: { color: 'green', label: reviewText('workflow.ready', 'Ready') },
    [CAPTURE_SESSION_STATUSES.syncFailed]: { color: 'red', label: reviewText('workflow.syncFailed', 'Sync failed') },
    [CAPTURE_SESSION_STATUSES.syncing]: { color: 'blue', label: reviewText('workflow.syncing', 'Syncing') },
    [CAPTURE_SESSION_STATUSES.synced]: { color: 'green', label: reviewText('workflow.synced', 'Synced') },
  };

  return statusBySessionStatus[status];
}

function useLiveTimeSpentMs(session: CaptureSession) {
  const sessionId = session.id;
  const mountedAtRef = useRef(Date.now());
  const [now, setNow] = useState(() => Date.now());
  const storedTimeSpentMs = (session.data.timeSpentMs as number | undefined) ?? 0;

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    mountedAtRef.current = Date.now();
    setNow(Date.now());
  }, [sessionId]);

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(Date.now()), 15_000);

    return () => window.clearInterval(intervalId);
  }, []);

  if (session.status === CAPTURE_SESSION_STATUSES.queued || session.status === CAPTURE_SESSION_STATUSES.synced) {
    return storedTimeSpentMs;
  }

  return storedTimeSpentMs + Math.max(0, now - mountedAtRef.current);
}

function formatDuration(durationMs: number) {
  if (durationMs <= 0) {
    return '<1m';
  }

  return `${Math.max(1, Math.round(durationMs / 60_000))}m`;
}
